/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { setCurentStep } from 'api/client-side-slices/DesignSlice';
import {
  useGetEntityTypesQuery,
  useLazyGetEntityByIdQuery,
  usePrimeSearchMutation,
} from 'api/EntitiesApiSlice';
import { useAddIndividualMutation } from 'api/IndividualsApiSlice';
import { useAddNewTrustMutation, useUploadDocumentsMutation } from 'api/TrustApiSlice';
import { useAppDispatch, useAppSelector } from 'app-redux/hooks';
import { ModalComponent, useModal } from 'components';
import { useDrawer } from 'components/Drawer';
import { format } from 'date-fns';
import { useToast } from 'providers/ToastProvider';
import { CreatePersonsWithEntityModel } from 'solstice-types';
import { removeWhiteSpaces } from 'utils';
import { IndividualDrawerFooter } from '../AddNewIndividual/IndividualDrawerFooter';
import { AddTrustIndividualsStep } from './Steps/AddTrustIndividualsStep';
import { AddTrustStep } from './Steps/AddTrustStep';
import { TrustSearchStep } from './Steps/TrustSearchStep';

enum Step {
  SEARCH_TRUST,
  ADD_TRUST,
  ADD_INDIVIDUALS,
}

export const AddNewTrustFlow = () => {
  const [currentStep, setCurrentStep] = useState<Step>(Step.SEARCH_TRUST);
  const [stepHistory, setStepHistory] = useState<Step[]>([Step.SEARCH_TRUST]);
  const [error, setError] = useState<string | null>(null);
  const [forceStorePerson, setForceStorePerson] = useState<boolean>(false);

  const [addIndividuals, { isLoading: areIndividualsLoading }] = useAddIndividualMutation();
  const [addNewTrust, { isLoading: isAddNewTrustLoading }] = useAddNewTrustMutation();

  const [searchPrime, { data: primeData, isLoading: isGroupSearchLoading }] =
    usePrimeSearchMutation();

  const [fetchEntity, { isLoading: isEntityLoading }] = useLazyGetEntityByIdQuery();
  const [uploadTrustDocuments, { isLoading: areDocumentsLoading }] = useUploadDocumentsMutation();

  const { existingCompany, uboReportData } = useAppSelector((state) => state.groups);
  const { closeDrawer } = useDrawer();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, formState, watch, getValues } = methods;

  const { isValid } = formState;

  const { data: entityTypes } = useGetEntityTypesQuery();

  const entityTypesData = entityTypes?.filter((entity) => entity.name === 'Trust') ?? [];
  const entityId = entityTypesData?.[0]?.id ?? '';

  const { formTrustType, formTrustName } = getValues();

  const renderButtonText = () => {
    if (existingCompany) {
      return 'Start Verification';
    }
    return currentStep === Step.ADD_TRUST ? 'Continue' : 'Start Verification';
  };

  const goToNextStep = (nextStep: Step) => {
    setCurrentStep(nextStep);
    setStepHistory((prevHistory) => [...prevHistory, nextStep]);
  };

  const goToPreviousStep = () => {
    dispatch(setCurentStep(0));

    setStepHistory((prevHistory) => {
      if (prevHistory.length > 1) {
        const updatedHistory = prevHistory.slice(0, -1);
        setCurrentStep(updatedHistory[updatedHistory.length - 1]);
        return updatedHistory;
      }
      return prevHistory;
    });
  };

  const formUploadedFiles = watch('uploaded_files');

  const addTrustIndividuals = async (data: any) => {
    try {
      await addIndividuals(data).unwrap();
      closeDrawer();
      navigate(`/entity-details/${data.newIndividual[0].entityId}`);

      toast.success('Trust added successfully!');
    } catch (error: any) {
      let errorMessage = '';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'data' in error) {
        const { data } = error as { data: string | object };
        errorMessage =
          typeof data === 'string' ? data.toLowerCase().trim() : JSON.stringify(data, null, 2);
      }

      if (errorMessage.includes('person with email:')) {
        openModal(
          <ModalComponent
            bodyContent={modalBodyText}
            isLoading={areIndividualsLoading}
            onCancel={closeModal}
            onUpdate={() => {
              setForceStorePerson(!forceStorePerson);
              setTimeout(() => {
                onSubmit(getValues());
              }, 2000);
            }}
          />,
          'Duplicated Email'
        );
      } else if (errorMessage.includes('to register for prime kyc access')) {
        openModal(
          <ModalComponent
            bodyContent={preventVerificationText}
            onCancel={closeModal}
            hasOneButton={true}
            buttonText="Close"
            onUpdate={() => {
              closeModal();
            }}
          />,
          'Verification failed'
        );
      } else {
        toast.error('A general error has occurred, please contact IT support for assistance.');
      }
    }
  };

  const { openModal, closeModal } = useModal();

  const modalBodyText =
    'This email address is already used by another individual in your Prime KYC account. Cancel to change the email, or create a new verification using it.';

  const preventVerificationText = (
    <>
      To register for Prime KYC access, please contact{' '}
      <a href="mailto:support@primecc.com.au" className="text-blue-500">
        support@primecc.com.au
      </a>
    </>
  );

  const onSubmit = async (data: any, forceStoreOverride?: boolean) => {
    const forceStore = forceStoreOverride ?? forceStorePerson;
    if (currentStep === Step.SEARCH_TRUST && existingCompany) {
      dispatch(setCurentStep(0));

      const response = await fetchEntity({ id: existingCompany.id ?? '' }).unwrap();

      if (!response || !response.persons) {
        return;
      }

      const personsData = response.persons.map((person: any) => ({
        email: person.email || '',
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        middleName: person.middleName || '',
        mobilePhone: person.mobilePhone || '',
        entityId: response.id || '',
        dateofBirth: person.dateOfBirth || '',
        roleId: person.roleId || '',
        ownershipPercentage: person.ownershipPercentage || 0,
        beneficiallyHeld: person.beneficiallyHeld ?? false,
        forceStore: forceStore,
        country: person.country || '',
        postcode: person.postcode || '',
        state: person.state || '',
        streetName: person.streetName || '',
        streetNumber: person.streetNumber || '',
        streetType: person.streetType || '',
        suburb: person.suburb || '',
      }));

      addTrustIndividuals({ newIndividual: personsData, isGroup: false });
    } else if (currentStep === Step.ADD_TRUST) {
      goToNextStep(Step.ADD_INDIVIDUALS);
      dispatch(setCurentStep(1));
    } else {
      const formValues = getValues();
      dispatch(setCurentStep(2));

      const formattedData = Object.keys(formValues)
        .filter((key) => key.startsWith('firstName_'))
        .map((key, index) => {
          const i = key.split('_')[1];
          const rawDate = formValues[`dateOfBirth_${i}`];
          const addressComponents = formValues[`address_${i}`];

          const { streetNumber, streetName, streetType, suburb, state, postcode, country } =
            addressComponents;

          return {
            firstName: formValues[`firstName_${i}`] || '',
            lastName: formValues[`lastName_${i}`] || '',
            middleName: formValues[`middleName_${i}`] || '',
            email: formValues[`email_${i}`] || '',
            dateOfBirth: rawDate
              ? format(new Date(rawDate.$d || rawDate), 'yyyy-MM-dd')
              : undefined,
            mobilePhone: removeWhiteSpaces(formValues[`mobilePhone_${i}`] || ''),
            ownershipPercentage: Number(formValues[`ownership_${i}`]) || 0,
            roleId: formValues[`role_${i}`] || '',
            forceStore: forceStore || false,
            country: country || '',
            postcode: postcode || '',
            state: state || '',
            streetName: streetName || '',
            streetNumber: streetNumber || '',
            streetType: streetType || '',
            suburb: suburb || '',
          };
        })
        .filter((person) => person.firstName?.trim());

      const formData: CreatePersonsWithEntityModel = {
        persons: formattedData,
        entityTypeId: entityId,
        entityRegisteredTypeId: formTrustType,
        registeredName: formTrustName,
      };

      try {
        await addNewTrust({ newTrust: formData })
          .unwrap()
          .then((response: any) => {
            uploadTrustDocuments({
              entityId: response.entity.id,
              files: formUploadedFiles,
            })
              .unwrap()
              .then(() => {
                navigate(`/entity-details/${response.entity.id}`);
                closeDrawer();
                toast.success('Trust added and files uploaded successfully!');
              });
          });
      } catch (error: any) {
        let errorMessage = '';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'data' in error) {
          const { data } = error as { data: string | object };
          errorMessage =
            typeof data === 'string' ? data.toLowerCase().trim() : JSON.stringify(data, null, 2);
        }

        if (errorMessage.includes('person with email:')) {
          openModal(
            <ModalComponent
              bodyContent={modalBodyText}
              isLoading={areIndividualsLoading}
              onCancel={closeModal}
              onUpdate={() => {
                setForceStorePerson((prev) => !prev);
                closeModal();
                setTimeout(() => {
                  onSubmit(getValues(), !forceStorePerson);
                }, 2000);
              }}
            />,
            'Duplicated Email'
          );
        } else if (errorMessage.includes('to register for prime kyc access')) {
          openModal(
            <ModalComponent
              bodyContent={preventVerificationText}
              onCancel={closeModal}
              hasOneButton={true}
              buttonText="Close"
              onUpdate={() => {
                closeModal();
              }}
            />,
            'Verification failed'
          );
        } else if (errorMessage.includes('was not found in this key vault')) {
          openModal(
            <ModalComponent
              bodyContent={preventVerificationText}
              onCancel={closeModal}
              hasOneButton={true}
              buttonText="Close"
              onUpdate={() => {
                closeModal();
              }}
            />,
            'Verification failed'
          );
        } else {
          toast.error('A general error has occurred, please contact IT support for assistance.');
        }
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case Step.SEARCH_TRUST:
        return (
          <TrustSearchStep
            setStep={goToNextStep}
            isLoading={isGroupSearchLoading}
            type="Trust"
            searchGroup={searchPrime}
            primeData={primeData}
          />
        );
      case Step.ADD_TRUST:
        return <AddTrustStep setStep={setCurrentStep} error={error ?? ''} />;
      case Step.ADD_INDIVIDUALS:
        return (
          <AddTrustIndividualsStep setStep={setCurrentStep} uploadedFiles={formUploadedFiles} />
        );
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods} handleSubmit={() => handleSubmit((data) => onSubmit(data))}>
      {renderStep()}
      <IndividualDrawerFooter
        confirmButtonText={renderButtonText}
        isLoading={
          isEntityLoading || areIndividualsLoading || isAddNewTrustLoading || areDocumentsLoading
        }
        handlePreviousStep={goToPreviousStep}
        hasSteps={currentStep === Step.ADD_INDIVIDUALS}
        handleSave={() => handleSubmit((data) => onSubmit(data, forceStorePerson))()}
        isStartDisabled={(currentStep === 1 && !isValid) || formUploadedFiles?.length === 0}
      />
    </FormProvider>
  );
};
